<template>
  <div
    v-if="!item.meta || !item.meta.hidden"
    :class="[
      'menu-wrapper',
      isCollapse ? 'simple-mode' : 'full-mode',
      { 'first-level': isFirstLevel }
    ]"
  >
    <template v-if="theOnlyOneChild && !theOnlyOneChild.children">
      <SidebarItemLink
        v-if="theOnlyOneChild.meta"
        :to="resolvePath(theOnlyOneChild.path)"
      >
        <el-menu-item
          :index="resolvePath(theOnlyOneChild.path)"
          :class="{
            'submenu-title-noDropdown': isFirstLevel,
            'pulsated-text': theOnlyOneChild.meta.highlight
          }"
        >
          <el-icon v-if="theOnlyOneChild.meta?.icon || item.meta?.icon">
            <font-awesome-icon
              :icon="theOnlyOneChild.meta?.icon || item.meta?.icon"
            />
          </el-icon>
          <template v-if="theOnlyOneChild.meta?.title" #title>
            <span>{{ theOnlyOneChild.meta.title }}</span>
          </template>
        </el-menu-item>
      </SidebarItemLink>
    </template>
    <el-sub-menu v-else :index="resolvePath(item.path)" popper-append-to-body>
      <template #title>
        <el-icon>
          <font-awesome-icon v-if="item.meta?.icon" :icon="item.meta.icon" />
        </el-icon>
        <span v-if="item.meta?.title">{{ item.meta.title }}</span>
      </template>
      <template v-if="children">
        <sidebar-item
          v-for="child in children"
          :key="child.path"
          :item="child"
          :is-collapse="isCollapse"
          :is-first-level="false"
          :base-path="resolvePath(child.path)"
          class="nest-menu"
        />
      </template>
    </el-sub-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { RouteConfig } from 'vue-router'
import { isExternal } from '@/utils/validate'
import SidebarItemLink from './SidebarItemLink.vue'

export default defineComponent({
  name: 'SidebarItem',
  components: {
    SidebarItemLink
  },
  props: {
    item: {
      type: Object as PropType<RouteConfig>,
      required: true
    },
    isCollapse: {
      type: Boolean,
      default: false
    },
    isFirstLevel: {
      type: Boolean,
      default: true
    },
    basePath: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const showingChildNumber = computed(() => {
      if (props.item.children) {
        return children.value.length
      }
      return 0
    })

    const children = computed(() => {
      if (!props.item || !props.item.children) {
        return []
      }
      return props.item.children.filter((item) => {
        if (item.meta) {
          if (item.meta.hidden) {
            return false
          } else {
            return true
          }
        } else {
          return true
        }
      })
    })

    const theOnlyOneChild = computed(() => {
      if (showingChildNumber.value > 1) {
        return null
      } else if (showingChildNumber.value === 1) {
        return children.value[0]
      }
      // If there is no children, return itself with path removed,
      // because this.basePath already contains item's path information
      return { ...props.item, path: '' }
    })

    const resolvePath = (routePath: string) => {
      if (isExternal(routePath)) {
        return routePath
      }
      if (isExternal(props.basePath)) {
        return props.basePath
      }

      if (routePath[0] === '/') {
        return routePath
      } else {
        return props.basePath
      }
    }

    return { theOnlyOneChild, resolvePath, children }
  }
})
</script>

<style lang="scss">
.el-sub-menu.is-active > .el-sub-menu__title {
  color: var(--submenu-active-color) !important;
}

.full-mode {
  .nest-menu .el-sub-menu > .el-sub-menu__title,
  .el-sub-menu .el-menu-item {
    min-width: var(--side-bar-width) !important;
    background-color: var(--side-bar-menu-bg-color) !important;

    &:hover {
      background-color: var(--side-bar-menu-hover-color) !important;
    }
  }
}

.simple-mode {
  &.first-level {
    .sub-menu-title-noDropdown {
      padding: 0 !important;
      position: relative;

      .el-tooltip {
        padding: 0 !important;
      }
    }

    .el-sub-menu {
      overflow: hidden;

      & > .el-sub-menu__title {
        .el-sub-menu__icon-arrow {
          display: none;
        }

        & > span {
          visibility: hidden;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.el-menu-item,
.el-sub-menu {
  svg {
    margin-right: 16px;
  }
}
</style>
